import { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import {
  Users, Star, Mail, Send, RefreshCcw, Search,
  ShoppingBag, DollarSign, X, Check, MessageSquare, Tag,
} from "lucide-react";

const formatAud  = (v) => `$${Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";

const CUSTOMER_TAGS = ["VIP", "Trade", "Wholesale", "Repeat", "B2B"];
const TAG_COLORS = {
  "VIP":       "bg-amber-100 text-amber-700 border-amber-200",
  "Trade":     "bg-blue-100 text-blue-700 border-blue-200",
  "Wholesale": "bg-purple-100 text-purple-700 border-purple-200",
  "Repeat":    "bg-green-100 text-green-700 border-green-200",
  "B2B":       "bg-orange-100 text-orange-700 border-orange-200",
};

// ─── Inline tag editor ─────────────────────────────────────────────────
function CustomerTagsCell({ customer, onUpdate }) {
  const [open, setOpen] = useState(false);
  const tags = customer.tags || [];

  const toggleTag = async (tag) => {
    const next = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag];
    try {
      await adminApi.put(`/admin/customers/${customer.user_id}/tags`, { tags: next });
      onUpdate();
    } catch { toast.error("Failed to update tags"); }
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1 mb-1">
        {tags.map((t) => (
          <span key={t}
            className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-md border font-bold ${TAG_COLORS[t] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>
            {t}
          </span>
        ))}
      </div>
      <button onClick={() => setOpen(!open)}
        className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-md transition-colors"
        style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#64748b" }}>
        + Tags
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-20 bg-white rounded-xl p-2 flex flex-wrap gap-1 min-w-[160px] shadow-lg"
          style={{ border: "1px solid #dde3ef" }}>
          {CUSTOMER_TAGS.map((tag) => (
            <button key={tag} onClick={() => toggleTag(tag)}
              className={`text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-lg border transition-all ${
                tags.includes(tag)
                  ? TAG_COLORS[tag] + " font-bold"
                  : "bg-zinc-50 text-zinc-400 border-zinc-200 hover:border-zinc-300"
              }`}>
              {tags.includes(tag) ? "✓ " : ""}{tag}
            </button>
          ))}
          <button onClick={() => setOpen(false)}
            className="w-full text-[9px] font-mono uppercase tracking-widest mt-1 text-right"
            style={{ color: "#94a3b8" }}>
            Done
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Email modal ───────────────────────────────────────────────────────
function EmailModal({ selected, allCount, onClose, onSent }) {
  const [type,   setType]   = useState("review");
  const [subject,setSubject]= useState("");
  const [body,   setBody]   = useState("");
  const [code,   setCode]   = useState("");
  const [busy,   setBusy]   = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!subject.trim() && type !== "review") { toast.error("Subject required"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/customers/bulk-email", {
        customer_ids:  selected.length > 0 ? selected : [],
        subject:       type === "review" ? (subject || "Would you leave us a Google review? ⭐") : subject,
        body, email_type: type,
        discount_code: type === "promo" ? code : null,
      });
      setResult(data);
      toast.success(`Sent to ${data.sent} customers`);
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  const inputClass = "w-full px-3 py-2 rounded-xl text-[13px] outline-none";
  const inputStyle = { background: "#f5f7fb", border: "1px solid #dde3ef", color: "#0f172a" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl" style={{ border: "1px solid #dde3ef" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-heading text-xl font-bold" style={{ color: "#0f172a" }}>Send Email</div>
            <div className="text-[12px] font-mono mt-0.5" style={{ color: "#94a3b8" }}>
              {selected.length > 0 ? `${selected.length} selected customer${selected.length !== 1 ? "s" : ""}` : `All ${allCount} customers`}
            </div>
          </div>
          <button onClick={onClose} style={{ color: "#94a3b8" }}><X className="h-5 w-5" /></button>
        </div>

        {result ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "#dcfce7" }}>
              <Check className="h-6 w-6" style={{ color: "#16a34a" }} />
            </div>
            <div className="font-heading font-bold text-[18px] mb-2" style={{ color: "#0f172a" }}>Emails Sent</div>
            <div className="text-[13px]" style={{ color: "#64748b" }}>
              {result.sent} sent · {result.failed} failed · {result.total} total
            </div>
            <button onClick={onClose}
              className="mt-5 px-5 py-2 rounded-xl text-[12px] font-mono uppercase tracking-widest"
              style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
              Close
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Type selector */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "review", label: "Google Review", icon: "⭐" },
                { v: "promo",  label: "Promotion",     icon: "🏷" },
                { v: "custom", label: "Custom",        icon: "✉️" },
              ].map(({ v, label, icon }) => (
                <button key={v} onClick={() => setType(v)}
                  className="h-14 rounded-xl border-2 flex flex-col items-center justify-center gap-0.5 transition-all text-[11px] font-mono uppercase tracking-widest"
                  style={type === v
                    ? { borderColor: "#e31e24", background: "#fef2f2", color: "#e31e24" }
                    : { borderColor: "#dde3ef", background: "#f5f7fb", color: "#64748b" }
                  }>
                  <span className="text-lg">{icon}</span>
                  {label}
                </button>
              ))}
            </div>

            {/* Subject */}
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest block mb-1.5" style={{ color: "#94a3b8" }}>
                Subject {type === "review" && <span style={{ color: "#94a3b8" }}>(optional)</span>}
              </label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder={type === "review" ? "Would you leave us a Google review? ⭐" : type === "promo" ? "Exclusive offer for BCP customers…" : "Subject"}
                className={inputClass} style={{ ...inputStyle, height: 40 }} />
            </div>

            {/* Body */}
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest block mb-1.5" style={{ color: "#94a3b8" }}>
                {type === "review" ? "Custom message (optional)" : "Message body"}
              </label>
              <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={type === "review" ? "Leave blank for default review message" : "Your message here…"}
                className={inputClass + " resize-none"} style={inputStyle} />
            </div>

            {type === "promo" && (
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest block mb-1.5" style={{ color: "#94a3b8" }}>
                  Discount code (optional)
                </label>
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="SAVE10" className={inputClass + " font-mono"}
                  style={{ ...inputStyle, height: 40 }} />
              </div>
            )}

            {type === "review" && (
              <div className="rounded-xl px-3 py-2.5 text-[12px]"
                style={{ background: "#eff6ff", border: "1px solid #93c5fd", color: "#1e40af" }}>
                Uses Google Review link from <strong>Settings → Notifications</strong>.
              </div>
            )}

            <button onClick={send} disabled={busy}
              className="w-full h-11 rounded-xl text-white font-heading font-bold uppercase tracking-wide text-[14px] flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              style={{ background: "#e31e24" }}>
              <Send className="h-4 w-4" />
              {busy ? "Sending…" : `Send to ${selected.length > 0 ? selected.length : allCount} customer${(selected.length || allCount) !== 1 ? "s" : ""}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Customer detail drawer ────────────────────────────────────────────
function CustomerDrawer({ customer, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    adminApi.get(`/admin/customers/${customer.user_id}`)
      .then((r) => setDetail(r.data)).catch(() => {});
  }, [customer.user_id]);

  const STATUS_BADGE = {
    paid:      "bg-green-100 text-green-700 border-green-200",
    new:       "bg-amber-100 text-amber-700 border-amber-200",
    shipped:   "bg-blue-100 text-blue-700 border-blue-200",
    cancelled: "bg-red-100 text-red-700 border-red-200",
    confirmed: "bg-indigo-100 text-indigo-700 border-indigo-200",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-end" style={{ background: "rgba(0,0,0,0.45)" }} onClick={onClose}>
      <div
        className="bg-white h-full w-full max-w-md overflow-y-auto p-6 shadow-2xl"
        style={{ borderLeft: "1px solid #dde3ef" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="font-heading text-xl font-bold" style={{ color: "#0f172a" }}>Customer Profile</div>
          <button onClick={onClose} style={{ color: "#94a3b8" }}><X className="h-5 w-5" /></button>
        </div>

        {/* Avatar + name */}
        <div className="flex items-center gap-4 mb-6">
          {customer.picture
            ? <img src={customer.picture} alt="" className="w-14 h-14 rounded-2xl object-cover" />
            : <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-heading font-black text-xl text-white"
                style={{ background: "#1e5bb8" }}>
                {(customer.name || "?")[0].toUpperCase()}
              </div>
          }
          <div>
            <div className="font-heading font-bold text-[18px]" style={{ color: "#0f172a" }}>{customer.name || "—"}</div>
            <div className="text-[13px]" style={{ color: "#64748b" }}>{customer.email}</div>
            {customer.phone && <div className="text-[12px]" style={{ color: "#64748b" }}>{customer.phone}</div>}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: "Orders",     value: customer.order_count || 0 },
            { label: "Total Spent",value: formatAud(customer.total_spent) },
            { label: "Last Order", value: customer.last_order_at ? formatDate(customer.last_order_at) : "—" },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-xl p-3 text-center" style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
              <div className="font-heading font-bold text-[16px]" style={{ color: "#0f172a" }}>{value}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest mt-0.5" style={{ color: "#94a3b8" }}>{label}</div>
            </div>
          ))}
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest mb-3 pb-2 font-bold"
          style={{ color: "#94a3b8", borderBottom: "1px solid #f1f5f9" }}>
          Order History
        </div>

        {detail ? (
          detail.orders.length === 0 ? (
            <div className="text-[13px] text-center py-6" style={{ color: "#94a3b8" }}>No orders yet</div>
          ) : (
            <div className="space-y-2">
              {detail.orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between p-3 rounded-xl"
                  style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
                  <div>
                    <div className="font-mono text-[12px] font-bold" style={{ color: "#0f172a" }}>{o.order_number}</div>
                    <div className="text-[11px]" style={{ color: "#94a3b8" }}>{formatDate(o.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-bold text-[14px]" style={{ color: "#0f172a" }}>{formatAud(o.total)}</div>
                    <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-md border ${STATUS_BADGE[o.status] || "bg-zinc-100 text-zinc-500 border-zinc-200"}`}>
                      {o.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="text-[12px] text-center py-4 font-mono animate-pulse" style={{ color: "#94a3b8" }}>Loading…</div>
        )}

        <div className="mt-6 pt-4 text-[11px] font-mono" style={{ borderTop: "1px solid #f1f5f9", color: "#94a3b8" }}>
          Registered {formatDate(customer.created_at)}
          {customer.auth_provider && <> · via {customer.auth_provider}</>}
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────
export default function AdminCustomers() {
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [q,         setQ]         = useState("");
  const [selected,  setSelected]  = useState(new Set());
  const [emailOpen, setEmailOpen] = useState(false);
  const [drawer,    setDrawer]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/customers");
      setItems(data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = q
    ? items.filter((c) => [c.name, c.email, c.phone].some((v) => v?.toLowerCase().includes(q.toLowerCase())))
    : items;

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.user_id)));
  };
  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const totalSpend  = items.reduce((s, c) => s + (c.total_spent  || 0), 0);
  const totalOrders = items.reduce((s, c) => s + (c.order_count  || 0), 0);

  return (
    <div className="space-y-5" data-testid="admin-customers-page">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color: "#94a3b8" }}>Audience</div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color: "#0f172a" }}>Customers</h1>
          <p className="text-[12px] mt-0.5" style={{ color: "#64748b" }}>{items.length} registered customers</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading}
            className="flex items-center gap-2 px-4 h-9 rounded-xl text-[13px] font-semibold transition-colors"
            style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={() => setEmailOpen(true)}
            className="flex items-center gap-2 px-4 h-9 rounded-xl text-white text-[13px] font-semibold"
            style={{ background: "#e31e24" }}>
            <Send className="h-3.5 w-3.5" />
            {selected.size > 0 ? `Email ${selected.size} selected` : "Email all"}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Users,       label: "Customers",    value: items.length,                              color: "#0f172a",  iconBg: "#eff6ff",  iconColor: "#1e5bb8" },
          { icon: ShoppingBag, label: "Total orders",  value: totalOrders,                              color: "#1e5bb8",  iconBg: "#eff6ff",  iconColor: "#1e5bb8" },
          { icon: DollarSign,  label: "Total spend",   value: formatAud(totalSpend),                    color: "#16a34a",  iconBg: "#f0fdf4",  iconColor: "#16a34a" },
          { icon: Mail,        label: "With email",    value: items.filter((c) => c.email).length,      color: "#d97706",  iconBg: "#fffbeb",  iconColor: "#d97706" },
        ].map(({ icon: Icon, label, value, color, iconBg, iconColor }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: iconBg }}>
                <Icon className="h-4 w-4" style={{ color: iconColor }} />
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>{label}</div>
            </div>
            <div className="font-heading font-bold text-[22px]" style={{ color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Search + bulk actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: "#94a3b8" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…"
            className="w-full pl-9 h-9 pr-3 rounded-xl text-[13px] outline-none"
            style={{ background: "#ffffff", border: "1px solid #dde3ef", color: "#0f172a" }} />
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-medium" style={{ color: "#64748b" }}>{selected.size} selected</span>
            <button onClick={() => setSelected(new Set())}
              className="text-[11px] font-mono hover:underline" style={{ color: "#94a3b8" }}>Clear</button>
            <button onClick={() => setEmailOpen(true)}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11px] font-semibold"
              style={{ background: "#fef2f2", color: "#e31e24", border: "1px solid #fca5a5" }}>
              <Star className="h-3 w-3" /> Review Request
            </button>
            <button onClick={() => setEmailOpen(true)}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11px] font-semibold"
              style={{ background: "#eff6ff", color: "#1e5bb8", border: "1px solid #93c5fd" }}>
              <MessageSquare className="h-3 w-3" /> Send Promo
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm overflow-x-auto" style={{ border: "1px solid #dde3ef" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "#f5f7fb", borderBottom: "1px solid #dde3ef" }}>
              <th className="px-4 py-3 w-10">
                <input type="checkbox"
                  checked={selected.size === filtered.length && filtered.length > 0}
                  onChange={toggleAll} className="w-4 h-4 cursor-pointer accent-[#e31e24]" />
              </th>
              {["Customer", "Email", "Orders", "Total Spent", "Last Order", "Tags", "Registered", "Actions"].map((h, i) => (
                <th key={h}
                  className="py-3 px-4 font-mono font-semibold uppercase"
                  style={{ color: "#475569", fontSize: 10, letterSpacing: "0.15em", textAlign: [2,3].includes(i) ? "right" : "left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, idx) => (
              <tr key={c.user_id}
                className="hover:bg-[#f5f7fb] cursor-pointer transition-colors"
                style={{
                  borderBottom: idx < filtered.length - 1 ? "1px solid #f0f4f8" : "none",
                  background: selected.has(c.user_id) ? "#f0f4ff" : undefined,
                }}>

                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(c.user_id)} onChange={() => toggle(c.user_id)}
                    className="w-4 h-4 cursor-pointer accent-[#e31e24]" />
                </td>

                <td className="px-4 py-3" onClick={() => setDrawer(c)}>
                  <div className="flex items-center gap-2.5">
                    {c.picture
                      ? <img src={c.picture} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                      : <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-[12px] shrink-0"
                          style={{ background: "#1e5bb8" }}>
                          {(c.name || "?")[0].toUpperCase()}
                        </div>
                    }
                    <div>
                      <div className="font-medium text-[13px]" style={{ color: "#0f172a" }}>{c.name || "—"}</div>
                      {c.phone && <div className="text-[11px]" style={{ color: "#94a3b8" }}>{c.phone}</div>}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-3 text-[13px]" style={{ color: "#475569" }} onClick={() => setDrawer(c)}>{c.email}</td>

                <td className="px-4 py-3 text-right" onClick={() => setDrawer(c)}>
                  <span className="font-heading font-bold text-[15px]" style={{ color: "#0f172a" }}>{c.order_count || 0}</span>
                </td>

                <td className="px-4 py-3 text-right" onClick={() => setDrawer(c)}>
                  <span className="font-heading font-bold text-[14px]" style={{ color: (c.total_spent || 0) > 0 ? "#16a34a" : "#94a3b8" }}>
                    {formatAud(c.total_spent)}
                  </span>
                </td>

                <td className="px-4 py-3 text-[11px] font-mono" style={{ color: "#64748b" }} onClick={() => setDrawer(c)}>
                  {c.last_order_at ? formatDate(c.last_order_at) : "—"}
                </td>

                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <CustomerTagsCell customer={c} onUpdate={load} />
                </td>

                <td className="px-4 py-3 text-[11px] font-mono" style={{ color: "#94a3b8" }} onClick={() => setDrawer(c)}>
                  {formatDate(c.created_at)}
                </td>

                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => { setSelected(new Set([c.user_id])); setEmailOpen(true); }}
                    title="Send email"
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors ml-auto"
                    style={{ background: "#fef2f2", color: "#e31e24" }}>
                    <Mail className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}

            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="py-16 text-center">
                  <Users className="h-10 w-10 mx-auto mb-3" style={{ color: "#c5cfe3" }} />
                  <div className="text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                    {q ? "No customers match" : "No customers yet"}
                  </div>
                </td>
              </tr>
            )}

            {loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="py-12 text-center">
                  <div className="w-6 h-6 border-2 border-[#1e5bb8] border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {emailOpen && (
        <EmailModal
          selected={[...selected]}
          allCount={filtered.length}
          onClose={() => setEmailOpen(false)}
          onSent={() => setEmailOpen(false)}
        />
      )}
      {drawer && <CustomerDrawer customer={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}